/**
 * Segundo factor (TOTP) — llamadas al backend.
 *
 * Ninguna de estas funciones abre sesión por su cuenta salvo la que canjea el
 * desafío: ese es el punto del diseño. El primer factor —contraseña o PIN por
 * correo— solo devuelve un `desafioId`, y la sesión de Firebase aparece cuando
 * el código de la app se valida en el servidor.
 */
import { signInWithCustomToken, type User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../config/firebase';

/**
 * Lo que devuelve el primer factor.
 *
 * Tres desenlaces posibles, y el tercero es fácil de olvidar: si la persona
 * está EXENTA, el servidor entrega la sesión de una vez y no hay desafío que
 * canjear. Omitir ese caso fue exactamente el error que dejó a los usuarios
 * exentos atrapados en la pantalla del código mientras el backend les abría la
 * sesión una y otra vez.
 */
export interface ResultadoPrimerFactor {
  desafioId?: string;
  /** El usuario ya tiene segundo factor: hay que pedirle el código. */
  requiereCodigo?: boolean;
  /** Aún no lo tiene: hay que enrolarlo antes de dejarlo entrar. */
  requiereEnrolamiento?: boolean;
  /** Está exento: la sesión ya quedó abierta y se puede entrar directo. */
  entroDirecto?: boolean;
}

export interface DatosEnrolamiento {
  /** URI `otpauth://` para el QR. */
  uri: string;
  /** El mismo secreto en texto, para quien no pueda escanear. */
  secreto: string;
}

/** Paso 1 con contraseña. La verifica el servidor; el navegador no recibe sesión. */
export async function iniciarConPassword(
  email: string,
  password: string,
): Promise<ResultadoPrimerFactor> {
  const fn = httpsCallable(functions, 'portal_iniciarSesionPassword');
  const { data } = await fn({ email, password });
  return abrirSesionSiVino(data as ResultadoPrimerFactor & { token?: string });
}

/**
 * Si el servidor entregó un token —el caso de la persona exenta— se abre la
 * sesión aquí mismo. Que esto viva en el servicio y no en la pantalla evita
 * que cada camino de ingreso tenga que acordarse de hacerlo.
 */
async function abrirSesionSiVino(
  r: ResultadoPrimerFactor & { token?: string },
): Promise<ResultadoPrimerFactor> {
  if (!r.token) return r;
  const cred = await signInWithCustomToken(auth, r.token);
  // Refresh para que el ID token traiga los custom claims (role, salaIds).
  await cred.user.getIdToken(true);
  return { entroDirecto: true };
}

/** Paso 2: canjea el desafío por una sesión. Acepta el código de la app o uno de respaldo. */
export async function verificarCodigo(
  desafioId: string,
  codigo: string,
): Promise<{ user: User; usadoRespaldo: boolean }> {
  const fn = httpsCallable(functions, 'portal_verificarSegundoFactor');
  const { data } = await fn({ desafioId, codigo });
  const { token, usadoRespaldo } = data as { token: string; usadoRespaldo: boolean };
  const cred = await signInWithCustomToken(auth, token);
  // Forzar el refresh para que el ID token traiga los custom claims (role,
  // salaIds). Sin esto, AuthContext arranca sin rol y la pantalla se encicla.
  await cred.user.getIdToken(true);
  return { user: cred.user, usadoRespaldo };
}

/** Genera el secreto y devuelve el QR. Sirve con desafío (primer ingreso) o con sesión. */
export async function iniciarEnrolamiento(desafioId?: string): Promise<DatosEnrolamiento> {
  const fn = httpsCallable(functions, 'portal_iniciarEnrolamiento2FA');
  const { data } = await fn(desafioId ? { desafioId } : {});
  return data as DatosEnrolamiento;
}

/**
 * Confirma el enrolamiento con el primer código y entrega los códigos de
 * respaldo. Si venía de un desafío, además abre la sesión: ya probó los dos
 * factores y no tiene sentido pedirle el código otra vez.
 */
export async function confirmarEnrolamiento(
  codigo: string,
  desafioId?: string,
): Promise<{ codigosRespaldo: string[]; entro: boolean }> {
  const fn = httpsCallable(functions, 'portal_confirmarEnrolamiento2FA');
  const { data } = await fn(desafioId ? { codigo, desafioId } : { codigo });
  const { codigosRespaldo, token } = data as { codigosRespaldo: string[]; token?: string };
  if (token) {
    const cred = await signInWithCustomToken(auth, token);
    await cred.user.getIdToken(true);
  }
  return { codigosRespaldo, entro: !!token };
}

export interface EstadoDosFactores {
  activo: boolean;
  enroladoEn: number | null;
  ultimoUsoEn: number | null;
  codigosRespaldoRestantes: number;
}

/** Estado del propio segundo factor, para el perfil. */
export async function obtenerEstado(): Promise<EstadoDosFactores> {
  const fn = httpsCallable(functions, 'portal_estadoSegundoFactor');
  const { data } = await fn({});
  return data as EstadoDosFactores;
}

/** Un administrador quita el segundo factor de alguien que perdió su teléfono. */
export async function reiniciarSegundoFactor(uid: string, motivo: string): Promise<void> {
  const fn = httpsCallable(functions, 'portal_reiniciarSegundoFactor');
  await fn({ uid, motivo });
}

export interface Exencion {
  uid: string;
  email: string | null;
  nombre: string | null;
  rol: string | null;
  motivo: string;
  otorgadaPor: string | null;
  otorgadaEn: number | null;
}

/** Exenciones vigentes y los roles que nunca las admiten. */
export async function listarExenciones(): Promise<{ exenciones: Exencion[]; rolesSinExencion: string[] }> {
  const fn = httpsCallable(functions, 'portal_listarExencionesSegundoFactor');
  const { data } = await fn({});
  return data as { exenciones: Exencion[]; rolesSinExencion: string[] };
}

/** Exime a una persona. El backend rechaza los roles admin y supervisor. */
export async function eximirUsuario(uid: string, motivo: string): Promise<void> {
  const fn = httpsCallable(functions, 'portal_eximirSegundoFactor');
  await fn({ uid, motivo });
}

/** Retira la exención: vuelve a exigirse el segundo factor. */
export async function revocarExencion(uid: string): Promise<void> {
  const fn = httpsCallable(functions, 'portal_revocarExencionSegundoFactor');
  await fn({ uid });
}

export interface UsuarioSegundoFactor {
  uid: string;
  email: string | null;
  nombre: string | null;
  rol: string;
  activo: boolean;
  enroladoEn: number | null;
  ultimoUsoEn: number | null;
  codigosRespaldoRestantes: number;
  exento: boolean;
  motivoExencion: string | null;
  exencionOtorgadaEn: number | null;
}

export interface PadronSegundoFactor {
  usuarios: UsuarioSegundoFactor[];
  rolesSinExencion: string[];
  /** false para el supervisor: ve el estado pero no puede cambiarlo. */
  puedeAdministrar: boolean;
}

/** Padrón del portal con el estado del segundo factor de cada persona. */
export async function listarUsuarios(): Promise<PadronSegundoFactor> {
  const fn = httpsCallable(functions, 'portal_listarUsuariosSegundoFactor');
  const { data } = await fn({});
  return data as PadronSegundoFactor;
}
